import { RoomProps } from '../types/room';
import { RoomSchedule } from '../types/survey';

export type SetupScheduleRoom = Partial<RoomProps> & {
  areaFloor: number;
  areaWall: number;
  deleted: number;
  floor: string;
  id: string;
  name: string;
};

export function getCombinedRoomScheduleDisplayRoom<
  TScheduleRoom extends object,
  TRoomDoc extends { lastUpdated?: any },
>({
  roomScheduleEntry,
  roomDoc,
}: {
  roomScheduleEntry: TScheduleRoom;
  roomDoc: TRoomDoc;
}): Omit<TRoomDoc, keyof TScheduleRoom> &
  TScheduleRoom & { lastUpdated: TRoomDoc['lastUpdated'] } {
  return {
    ...roomDoc,
    ...roomScheduleEntry,
    lastUpdated: roomDoc.lastUpdated,
  };
}

export function getActiveAssetCount(room: Partial<RoomProps>) {
  return Math.max(
    Number(room.assetsNumCreated || 0) - Number(room.assetsNumDeleted || 0),
    0,
  );
}

function isRoomActive(room: Partial<RoomProps>) {
  return !room.deleted || Number(room.deleted) === 0;
}

function getSafeRoomScheduleEntryId(room: Partial<RoomProps>, index: number) {
  return `${String(`${room.name || 'room'}_${room.floor || 'floor'}`)
    .replace(/\s/g, '_')
    .replace(/[^\w\d_]/g, '')}_${index}`;
}

export function getRoomScheduleEntriesForSetup({
  roomScheduleData,
  excludeDeleted = false,
}: {
  roomScheduleData?: Partial<RoomProps>[];
  excludeDeleted?: boolean;
}) {
  const roomEntries: Partial<RoomProps>[] = [];

  (roomScheduleData || []).forEach((room, index) => {
    if (!room) {
      return;
    }
    if (excludeDeleted && room.deleted) {
      return;
    }
    const id = room.id || getSafeRoomScheduleEntryId(room, index);
    roomEntries.push({ ...room, id });
  });

  return roomEntries;
}

export function getActiveRoomScheduleCount(
  roomScheduleData?: Partial<RoomProps>[],
) {
  return getRoomScheduleEntriesForSetup({
    roomScheduleData,
    excludeDeleted: true,
  }).length;
}

function getUniqueFloors(rooms: Partial<RoomProps>[]) {
  return Array.from(
    new Set(rooms.map((room) => room.floor).filter(Boolean)),
  ) as string[];
}

function areStringArraysEqual(left: string[] = [], right: string[] = []) {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

export function getRoomScheduleWithImportedRooms({
  importedRooms,
  roomSchedule,
}: {
  importedRooms?: Partial<RoomProps>[];
  roomSchedule?: Partial<RoomSchedule>;
}) {
  if (!importedRooms?.length) {
    return roomSchedule;
  }

  let changed = false;
  const consolidatedRooms = roomSchedule?.data?.length
    ? [...roomSchedule.data]
    : [];

  importedRooms.forEach((room) => {
    const importedRoom = toSetupScheduleRoom(room);
    if (!importedRoom) {
      return;
    }

    const matchIndex = consolidatedRooms.findIndex(
      (existingRoom) =>
        existingRoom.floor === importedRoom.floor &&
        existingRoom.name === importedRoom.name,
    );

    if (matchIndex < 0) {
      consolidatedRooms.push(importedRoom);
      changed = true;
      return;
    }

    const matchingRoom = consolidatedRooms[matchIndex];
    if (Number(matchingRoom.deleted || 0) === 0 && !matchingRoom.imported) {
      consolidatedRooms[matchIndex] = {
        ...matchingRoom,
        imported: true,
      };
      changed = true;
    }
  });

  const floors = getUniqueFloors(consolidatedRooms);
  if (!changed && areStringArraysEqual(floors, roomSchedule?.floors)) {
    return roomSchedule;
  }

  return {
    ...roomSchedule,
    data: consolidatedRooms,
    floors,
  };
}

export function getRoomScheduleSetupDeletionConfirmationReason(
  room: Partial<RoomProps>,
) {
  if (room.imported) {
    return 'imported';
  }

  if (getActiveAssetCount(room) > 0) {
    return 'assets';
  }

  return undefined;
}

export function getRoomsOmittedFromScheduleReplacement({
  existingRooms,
  replacementRooms,
}: {
  existingRooms: Partial<RoomProps>[];
  replacementRooms: SetupScheduleRoom[];
}) {
  const replacementRoomIds = new Set(
    replacementRooms.map((room) => room.id).filter(Boolean),
  );
  const replacementRoomKeys = new Set(
    replacementRooms.map(
      (room) =>
        `${String(room.floor || '')
          .trim()
          .toLowerCase()}::${String(room.name || '')
          .trim()
          .toLowerCase()}`,
    ),
  );

  return existingRooms.filter((room) => {
    if (!isRoomActive(room)) {
      return false;
    }

    if (room.id && replacementRoomIds.has(room.id)) {
      return false;
    }

    const roomKey = `${String(room.floor || '')
      .trim()
      .toLowerCase()}::${String(room.name || '')
      .trim()
      .toLowerCase()}`;

    return !replacementRoomKeys.has(roomKey);
  });
}

function toSetupScheduleRoom(
  room: Partial<RoomProps>,
): SetupScheduleRoom | undefined {
  if (!room.id || !room.name || !room.floor) {
    return undefined;
  }

  return {
    areaFloor: Number(room.areaFloor || 0),
    areaWall: Number(room.areaWall || 0),
    deleted: Number(room.deleted || 0),
    floor: room.floor,
    id: room.id,
    ...(room.imported === undefined ? {} : { imported: room.imported }),
    name: room.name,
    ...(room.notes === undefined ? {} : { notes: room.notes }),
  };
}

export function markOmittedRoomsForDeletionInScheduleReplacement({
  existingRooms,
  replacementRooms,
  deletedAt,
}: {
  existingRooms: Partial<RoomProps>[];
  replacementRooms: SetupScheduleRoom[];
  deletedAt: number;
}) {
  return [
    ...replacementRooms,
    ...getRoomsOmittedFromScheduleReplacement({
      existingRooms,
      replacementRooms,
    })
      .map((room) => ({ ...room, deleted: deletedAt }))
      .map(toSetupScheduleRoom)
      .filter((room): room is SetupScheduleRoom => !!room),
  ];
}
